import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { REPORT_COLORS, REPORT_SERIES_PALETTE } from '@/components/reporting/reportChartTheme';

/**
 * The one themed recharts wrapper (Prompt F). Every reporting chart goes through
 * here so the whole screen shares a single gold/cream/ink palette drawn from the
 * app's semantic tokens - never recharts' default blues. Colours are `rgb(var(..))`
 * off the theme tokens, so they stay theme-aware. Palette lives in
 * reportChartTheme.ts so this file only exports components.
 */

const AXIS = 'rgb(var(--text-faint))';
const GRID = 'rgb(var(--surface-3))';

const TOOLTIP_STYLE = {
  background: 'rgb(var(--surface-1))',
  border: '1px solid rgb(var(--accent-gold-dim))',
  borderRadius: 8,
  color: 'rgb(var(--text-strong))',
  fontSize: 12,
} as const;

const AXIS_TICK = { fill: AXIS, fontSize: 11 } as const;
const LEGEND_STYLE = { fontSize: 11, color: 'rgb(var(--text-soft))' } as const;

export type ChartRow = Record<string, string | number>;

export interface ChartSeries {
  /** Data key on each row. */
  key: string;
  /** Legend/tooltip label. */
  name: string;
  /** Override colour; defaults to the palette by index. */
  color?: string;
}

export interface ReferenceMark {
  /** Horizontal line at this y value (e.g. a target). */
  y?: number;
  /** Vertical line at this x value (e.g. the "today" marker). */
  x?: string | number;
  label?: string;
  color?: string;
  dashed?: boolean;
}

interface CommonProps {
  data: readonly ChartRow[];
  xKey: string;
  series: readonly ChartSeries[];
  height?: number;
  references?: readonly ReferenceMark[];
  /** Hide the legend (single-series charts). */
  hideLegend?: boolean;
}

function colorFor(series: ChartSeries, index: number): string {
  return series.color ?? REPORT_SERIES_PALETTE[index % REPORT_SERIES_PALETTE.length]!;
}

function References({ references }: { references?: readonly ReferenceMark[] }) {
  if (!references) return null;
  return (
    <>
      {references.map((r, i) => (
        <ReferenceLine
          key={`ref-${i}`}
          x={r.x}
          y={r.y}
          stroke={r.color ?? REPORT_COLORS.goldDim}
          strokeDasharray={r.dashed === false ? undefined : '4 4'}
          label={
            r.label
              ? { value: r.label, position: 'insideTopRight', fill: AXIS, fontSize: 10 }
              : undefined
          }
        />
      ))}
    </>
  );
}

/** A themed multi-series line chart. Pass `references` for target / "today" lines. */
export function ReportLineChart({
  data,
  xKey,
  series,
  height = 240,
  references,
  hideLegend,
}: CommonProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data as ChartRow[]} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} stroke={AXIS} tickLine={false} />
        <YAxis tick={AXIS_TICK} stroke={AXIS} tickLine={false} width={40} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: REPORT_COLORS.goldDim }} />
        {hideLegend ? null : <Legend wrapperStyle={LEGEND_STYLE} />}
        <References references={references} />
        {series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={colorFor(s, i)}
            strokeWidth={2}
            dot={{ r: 2, fill: colorFor(s, i) }}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

interface BarProps extends CommonProps {
  /** Stack the series into one bar per x (e.g. ours vs artist revenue). */
  stacked?: boolean;
}

/** A themed multi-series bar chart. Set `stacked` for a stacked bar per x. */
export function ReportBarChart({
  data,
  xKey,
  series,
  height = 240,
  references,
  hideLegend,
  stacked,
}: BarProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data as ChartRow[]} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} stroke={AXIS} tickLine={false} />
        <YAxis tick={AXIS_TICK} stroke={AXIS} tickLine={false} width={40} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgb(var(--surface-3) / 0.4)' }} />
        {hideLegend ? null : <Legend wrapperStyle={LEGEND_STYLE} />}
        <References references={references} />
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            fill={colorFor(s, i)}
            stackId={stacked ? 'stack' : undefined}
            radius={stacked ? undefined : [3, 3, 0, 0]}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
